/**
 * Brave Shields / privacy browsers "farble" canvas getImageData() to defeat
 * fingerprinting. Konva hit-testing historically required exact pixel colors,
 * so selection/drag broke with Shields up. Konva ≥10.0.10 uses tolerant matching;
 * we still probe so we can warn if a blocker still breaks hit detection.
 */

/** True when getImageData returns values different from what we drew. */
export function isCanvasFarblingActive(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 2;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    ctx.fillStyle = 'rgb(12, 34, 56)';
    ctx.fillRect(0, 0, 2, 2);
    const data = ctx.getImageData(0, 0, 1, 1).data;
    return data[0] !== 12 || data[1] !== 34 || data[2] !== 56;
  } catch {
    return true;
  }
}

/**
 * Smoke-test Konva-style color hit maps: write a unique RGB and read it back.
 * Returns false if the round-trip is too corrupted for reliable hit IDs.
 */
export function canvasHitColorsReliable(): boolean {
  if (typeof document === 'undefined') return true;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 4;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    // Distinct "hit colors" like Konva uses (avoid 0/255 extremes).
    const colors: Array<[number, number, number]> = [
      [17, 88, 199],
      [201, 44, 67],
      [55, 170, 33],
    ];
    for (let i = 0; i < colors.length; i++) {
      const [r, g, b] = colors[i]!;
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(i, 0, 1, 1);
    }
    for (let i = 0; i < colors.length; i++) {
      const [r, g, b] = colors[i]!;
      const data = ctx.getImageData(i, 0, 1, 1).data;
      // Konva 10 tolerates small farbling deltas; fail only if heavily altered.
      const dr = Math.abs(data[0]! - r);
      const dg = Math.abs(data[1]! - g);
      const db = Math.abs(data[2]! - b);
      if (dr > 8 || dg > 8 || db > 8) return false;
    }
    return true;
  } catch {
    return false;
  }
}
